import { getBaseUrl } from '@pique/graphql';

import type {
  ImageSearchOptions,
  ImageSearchResult,
  ImageSource,
} from './types';

export class FreepikSource implements ImageSource {
  readonly name = 'freepik';
  private readonly searchCache = new Map<string, { results: ImageSearchResult[]; timestamp: number }>();
  private readonly CACHE_DURATION = 300000; // 5 minutes

  async search(options: ImageSearchOptions): Promise<ImageSearchResult[]> {
    // Check cache first
    const cacheKey = `${options.query}_${options.width}_${options.height}`;
    const cached = this.searchCache.get(cacheKey);
    if (cached && (Date.now() - cached.timestamp) < this.CACHE_DURATION) {
      return cached.results;
    }

    const url = new URL(getBaseUrl() + '/api/copilot/freepik/search');
    url.searchParams.set('term', options.query);

    interface FreepikResult {
      id: string;
      title: string;
      url: string;
      format?: string;
    }

    interface FreepikResponse {
      results: FreepikResult[];
    }

    try {
      const response = await fetch(url.toString());

      if (!response.ok) {
        console.warn(`Freepik API returned ${response.status}, trying alternatives`);
        return [];
      }

      const result: FreepikResponse = await response.json();

      if (!result.results || result.results.length === 0) {
        return [];
      }

      const searchResults: ImageSearchResult[] = result.results.map(r => ({
        url: r.url,
        id: r.id,
        title: r.title,
        description: `Free image from Freepik`,
        type: r.format === 'svg' ? 'svg' as const : 'raster' as const,
      }));

      // Cache the results
      this.searchCache.set(cacheKey, { results: searchResults, timestamp: Date.now() });

      return searchResults;
    } catch (error) {
      console.warn('Error fetching Freepik images:', error);
      return [];
    }
  }

}
